import type { FocusEvent, ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from './ErrorMessage';
import { Fieldset } from './Fieldset';
import { Hint } from './Hint';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy } from './cx';

export interface DateParts {
  day: string;
  month: string;
  year: string;
}

export const EMPTY_DATE: DateParts = { day: '', month: '', year: '' };

export interface DateInputProps {
  /** Id of the group; inputs are `{id}-day`, `{id}-month`, `{id}-year` (error summary links to `{id}-day`). */
  id: string;
  namePrefix?: string;
  legend: ReactNode;
  legendClassName?: string;
  legendIsPageHeading?: boolean;
  legendLang?: string;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** Which parts to highlight; all parts when an error is set and this is omitted. */
  errorParts?: (keyof DateParts)[];
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  value: DateParts;
  onChange: (value: DateParts) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  ariaDisabled?: boolean;
  extraDescribedBy?: string;
  beforeInputs?: ReactNode;
  formGroupClassName?: string;
  /** `bday` → bday-day/bday-month/bday-year autocomplete tokens. */
  autocompletePrefix?: string;
  dayRef?: Ref<HTMLInputElement>;
}

/**
 * govuk/components/date-input. Uses type="text" with inputmode="numeric" (current GOV.UK guidance) instead of the
 * type="number" of the 2.11 template, to avoid spinner/scroll value changes and validation quirks.
 */
export function DateInput({
  id,
  namePrefix,
  legend,
  legendClassName,
  legendIsPageHeading,
  legendLang,
  hint,
  hintLang,
  error,
  errorParts,
  required,
  value,
  onChange,
  onBlur,
  ariaDisabled,
  extraDescribedBy,
  beforeInputs,
  formGroupClassName,
  autocompletePrefix,
  dayRef,
}: DateInputProps) {
  const { t } = useTranslation();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const parts: { key: keyof DateParts; width: string }[] = [
    { key: 'day', width: 'govuk-input--width-2' },
    { key: 'month', width: 'govuk-input--width-2' },
    { key: 'year', width: 'govuk-input--width-4' },
  ];
  const highlighted = (key: keyof DateParts) => Boolean(error) && (!errorParts || errorParts.includes(key));
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Fieldset
        role="group"
        legend={
          <>
            {legend}
            <RequiredMarker required={required} />
          </>
        }
        legendClassName={legendClassName}
        legendIsPageHeading={legendIsPageHeading}
        legendLang={legendLang}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
      >
        {hint && (
          <Hint id={hintId} lang={hintLang}>
            {hint}
          </Hint>
        )}
        {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        {beforeInputs}
        <div className="govuk-date-input" id={id}>
          {parts.map((part) => {
            const inputId = `${id}-${part.key}`;
            return (
              <div className="govuk-date-input__item" key={part.key}>
                <div className="govuk-form-group">
                  <label className="govuk-label govuk-date-input__label" htmlFor={inputId}>
                    {t(`dateInput.${part.key}`)}
                  </label>
                  <input
                    ref={part.key === 'day' ? dayRef : undefined}
                    className={cx('govuk-input govuk-date-input__input', part.width, highlighted(part.key) && 'govuk-input--error')}
                    id={inputId}
                    name={namePrefix ? `${namePrefix}-${part.key}` : part.key}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={autocompletePrefix ? `${autocompletePrefix}-${part.key}` : 'off'}
                    value={value[part.key]}
                    readOnly={ariaDisabled}
                    aria-disabled={ariaDisabled ? true : undefined}
                    aria-invalid={highlighted(part.key) ? true : undefined}
                    onChange={(e) => onChange({ ...value, [part.key]: e.target.value })}
                    onBlur={onBlur}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Fieldset>
    </div>
  );
}

/** "2026-10-01" → parts; invalid/empty → empty parts. */
export function isoToParts(iso: string | null | undefined): DateParts {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  if (!m) return { ...EMPTY_DATE };
  return { day: String(Number(m[3])), month: String(Number(m[2])), year: m[1] ?? '' };
}

export type PartsResult = { kind: 'empty' } | { kind: 'invalid'; parts: (keyof DateParts)[] } | { kind: 'valid'; iso: string };

/** Validates day/month/year parts as a real calendar date. */
export function partsToIso(parts: DateParts): PartsResult {
  const d = parts.day.trim();
  const m = parts.month.trim();
  const y = parts.year.trim();
  if (!d && !m && !y) return { kind: 'empty' };
  const missing = (['day', 'month', 'year'] as const).filter((k) => !parts[k].trim() || !/^\d+$/.test(parts[k].trim()));
  if (missing.length) return { kind: 'invalid', parts: [...missing] };
  if (y.length !== 4) return { kind: 'invalid', parts: ['year'] };
  const day = Number(d);
  const month = Number(m);
  const year = Number(y);
  if (month < 1 || month > 12) return { kind: 'invalid', parts: ['month'] };
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return { kind: 'invalid', parts: ['day'] };
  return { kind: 'valid', iso: `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
}
